import { Button, Layout, Space } from 'antd';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import RegionSwitcher from './RegionSwitcher';
import GlobalLoader from './GlobalLoader';
import '../../css/style.css';

const { Header, Content } = Layout;

export default function AppLayout() {
  const { isAuthenticated, logout, isLoading, isAdmin, hasPermission, user } = useAuth();
  const { region } = useRegion();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Layout style={{ minHeight: '100vh', width: '100%', margin: 0, padding: 0 }}>
      <GlobalLoader />
      <Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/" className="header-logo">
          <img src="/images/logo-white.png" alt="FulfillmentNinjas" style={{ maxWidth: '100%' }} />
        </Link>

        {isAuthenticated && !isLoading && (
          <nav>
            <button className="menu-toggle" aria-label="Toggle navigation">☰</button>
            <ul className="nav-links">
              {/* Everyday operator items — visible to any authenticated user
                  (regular 'user' role + admins). Needed so warehouse staff
                  can browse awaiting shipments, ship them, and print labels. */}
              <li>
                <NavLink to="/orders" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                  Orders
                </NavLink>
              </li>
              <li>
                <NavLink to="/uniuni-shipments" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                  Shipment
                </NavLink>
              </li>
              {/* Reports — admins bypass automatically via hasPermission;
                  regular users need `reports:read` granted in Admin UI. */}
              {hasPermission('reports:read') && (
                <li>
                  <NavLink to="/reports" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                    Reports
                  </NavLink>
                </li>
              )}
              {/* Admin-only configuration + management. */}
              {isAdmin && (
                <>
                  <li>
                    <NavLink to="/pickup-addresses" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                      Pickup Addresses
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/inventory-recon" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                      Inventory Recon
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/admin" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                      User
                    </NavLink>
                  </li>
                </>
              )}
            </ul>
          </nav>
        )}

        {isAuthenticated && !isLoading && (
          <Space size="middle">
            <RegionSwitcher />
            <span style={{ color: '#fff' }}>Hi, {user?.name ?? user?.email}</span>
            <Button danger onClick={handleLogout}>
              Logout
            </Button>
          </Space>
        )}
      </Header>
      <Content style={{ padding: 20 }}>
        {/*
         * `key={region ?? 'none'}` forces the entire routed page tree to remount
         * when the region changes. This is the simplest reliable way to refetch
         * ALL data on region switch — React Query invalidation only helps pages
         * that use React Query; plain useEffect+api patterns (like useStores)
         * would otherwise keep showing stale data from the previous region.
         *
         * Trade-off: local page state (open modals, scroll position, unsaved
         * form input) is discarded on switch. That's aligned with user intent
         * — switching regions is meant to be a clean context change.
         */}
        <Outlet key={region ?? 'none'} />
      </Content>
    </Layout>
  );
}
